import React, { useState } from 'react';
import { Button } from './Button';
import ContentChips from './ContentChips';
import { ArrowLeftIcon, BookOpenIcon, BrainIcon, HeadphonesIcon, SearchIcon, FilterIcon, TagIcon, MessageSquareIcon, MoonIcon, ActivityIcon, HeartIcon, UsersIcon, ClockIcon, PlusIcon } from 'lucide-react';
interface ThemeOverviewProps {
  onThemeSelect: (themeId: string) => void;
  onBackToHome: () => void;
}
const ThemeOverview: React.FC<ThemeOverviewProps> = ({
  onThemeSelect,
  onBackToHome
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  // Mock theme data
  const themes = [{
    id: 'stress',
    title: 'Stress & burn-out',
    description: 'Technieken om stress te herkennen, te verminderen en beter met druk om te gaan.',
    icon: <ActivityIcon className="w-5 h-5" data-id="element-1377" />,
    exerciseCount: 12,
    completedCount: 3,
    tags: ['stress', 'werk', 'ontspanning']
  }, {
    id: 'anxiety',
    title: 'Angst',
    description: 'Oefeningen om angst te begrijpen, te accepteren en ermee om te gaan.',
    icon: <BrainIcon className="w-5 h-5" data-id="element-1378" />,
    exerciseCount: 14,
    completedCount: 0,
    tags: ['angst', 'emoties', 'ontspanning']
  }, {
    id: 'sleep',
    title: 'Slaap',
    description: 'Verbeter je slaapkwaliteit, val makkelijker in slaap en word uitgerust wakker.',
    icon: <MoonIcon className="w-5 h-5" data-id="element-1379" />,
    exerciseCount: 8,
    completedCount: 2,
    tags: ['slaap', 'ontspanning', 'avondroutine']
  }, {
    id: 'focus',
    title: 'Focus & concentratie',
    description: 'Train je aandacht, verbeter je focus en wees productiever.',
    icon: <BookOpenIcon className="w-5 h-5" data-id="element-1380" />,
    exerciseCount: 10,
    completedCount: 1,
    tags: ['focus', 'werk', 'productiviteit']
  }, {
    id: 'relationships',
    title: 'Relaties',
    description: 'Verbeter je communicatie, stel grenzen en maak diepere verbindingen.',
    icon: <UsersIcon className="w-5 h-5" data-id="element-1381" />,
    exerciseCount: 7,
    completedCount: 0,
    tags: ['relaties', 'communicatie', 'sociaal']
  }, {
    id: 'mindfulness',
    title: 'Mindfulness',
    description: 'Leef meer in het moment en pieker minder door bewustzijnsoefeningen.',
    icon: <HeartIcon className="w-5 h-5" data-id="element-1382" />,
    exerciseCount: 15,
    completedCount: 5,
    tags: ['mindfulness', 'ontspanning', 'bewustzijn']
  }, {
    id: 'adhd',
    title: 'ADHD',
    description: 'Strategieën voor het omgaan met aandachtstekort en hyperactiviteit.',
    icon: <ActivityIcon className="w-5 h-5" data-id="element-1383" />,
    exerciseCount: 9,
    completedCount: 2,
    tags: ['adhd', 'focus', 'structuur']
  }, {
    id: 'trauma',
    title: 'Trauma & PTSS',
    description: 'Verwerk traumatische ervaringen en verminder PTSS-symptomen.',
    icon: <BrainIcon className="w-5 h-5" data-id="element-1384" />,
    exerciseCount: 11,
    completedCount: 0,
    tags: ['trauma', 'ptss', 'verwerking']
  }, {
    id: 'grief',
    title: 'Rouw & verlies',
    description: 'Leer omgaan met verlies en vind manieren om rouw te verwerken.',
    icon: <HeartIcon className="w-5 h-5" data-id="element-1385" />,
    exerciseCount: 8,
    completedCount: 1,
    tags: ['rouw', 'verlies', 'emoties']
  }, {
    id: 'perfectionism',
    title: 'Perfectionisme',
    description: 'Herken perfectionistische patronen en ontwikkel een gezondere mindset.',
    icon: <BookOpenIcon className="w-5 h-5" data-id="element-1386" />,
    exerciseCount: 7,
    completedCount: 0,
    tags: ['perfectionisme', 'zelfkritiek', 'acceptatie']
  }, {
    id: 'ocd',
    title: 'OCD',
    description: 'Technieken voor het omgaan met dwanggedachten en -handelingen.',
    icon: <ClockIcon className="w-5 h-5" data-id="element-1387" />,
    exerciseCount: 9,
    completedCount: 0,
    tags: ['ocd', 'dwang', 'angst']
  }, {
    id: 'autism',
    title: 'Autisme',
    description: 'Strategieën voor het omgaan met prikkels en sociale situaties.',
    icon: <UsersIcon className="w-5 h-5" data-id="element-1388" />,
    exerciseCount: 10,
    completedCount: 2,
    tags: ['autisme', 'prikkels', 'communicatie']
  }];
  // Filter themes based on search query and active filter
  const filteredThemes = themes.filter(theme => {
    const matchesSearch = theme.title.toLowerCase().includes(searchQuery.toLowerCase()) || theme.description.toLowerCase().includes(searchQuery.toLowerCase()) || theme.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    if (activeFilter === 'all') return matchesSearch;
    if (activeFilter === 'completed') return matchesSearch && theme.completedCount > 0;
    if (activeFilter === 'inprogress') return matchesSearch && theme.completedCount > 0 && theme.completedCount < theme.exerciseCount;
    if (activeFilter === 'notstarted') return matchesSearch && theme.completedCount === 0;
    // Filter by tag
    return matchesSearch && theme.tags.includes(activeFilter);
  });
  return <div className="w-full max-w-xl mx-auto py-8" data-id="element-1389">
      <div className="flex items-center mb-8" data-id="element-1390">
        <button onClick={onBackToHome} className="mr-4 p-2 rounded-full hover:bg-gray-100 border border-gray-300" aria-label="Terug" data-id="element-1391">
          <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-1392" />
        </button>
        <h1 className="text-2xl font-light text-gray-800" data-id="element-1393">Thema's</h1>
      </div>
      <div className="mb-6" data-id="element-1394">
        <div className="relative" data-id="element-1395">
          <input type="text" placeholder="Zoek thema's..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-gray-400 bg-white" data-id="element-1396" />
          <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500" data-id="element-1397" />
          <button className="absolute right-3 top-1/2 transform -translate-y-1/2 p-1 rounded-full hover:bg-gray-100" aria-label="Filter" data-id="element-1398">
            <FilterIcon className="w-5 h-5 text-gray-500" data-id="element-1399" />
          </button>
        </div>
      </div>
      <div className="mb-8" data-id="element-1400">
        <ContentChips activeChip={activeFilter} onChipClick={setActiveFilter} data-id="element-1401" />
      </div>
      <div className="space-y-4" data-id="element-1402">
        {filteredThemes.length > 0 ? filteredThemes.map(theme => <div key={theme.id} className="p-5 border-2 border-gray-300 border-dashed rounded-xl hover:bg-gray-50 cursor-pointer transition-colors duration-200" onClick={() => onThemeSelect(theme.id)} data-id="element-1403">
              <div className="flex items-start" data-id="element-1404">
                <div className="w-10 h-10 rounded-full border-2 border-gray-300 flex items-center justify-center mr-4 bg-white" data-id="element-1405">
                  {theme.icon}
                </div>
                <div className="flex-1" data-id="element-1406">
                  <h3 className="text-lg font-medium text-gray-800 mb-1" data-id="element-1407">
                    {theme.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2" data-id="element-1408">
                    {theme.description}
                  </p>
                  <div className="flex items-center justify-between" data-id="element-1409">
                    <div className="flex items-center" data-id="element-1410">
                      <div className="w-24 h-2 bg-gray-200 rounded-full mr-2" data-id="element-1411">
                        <div className="h-2 bg-gray-600 rounded-full" style={{
                    width: `${theme.completedCount / theme.exerciseCount * 100}%`
                  }} data-id="element-1412"></div>
                      </div>
                      <span className="text-xs text-gray-500" data-id="element-1413">
                        {theme.completedCount}/{theme.exerciseCount}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2" data-id="element-1414">
                      {theme.tags.slice(0, 2).map(tag => <span key={tag} className="px-2 py-1 bg-gray-100 border border-gray-200 rounded-full text-xs text-gray-700 flex items-center" data-id="element-1415">
                          <TagIcon className="w-3 h-3 mr-1" data-id="element-1416" />
                          {tag}
                        </span>)}
                    </div>
                  </div>
                </div>
              </div>
            </div>) : <div className="p-8 text-center border-2 border-gray-300 border-dashed rounded-xl" data-id="element-1417">
            <BookOpenIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" data-id="element-1418" />
            <h3 className="text-lg font-medium text-gray-700 mb-2" data-id="element-1419">
              Geen thema's gevonden
            </h3>
            <p className="text-sm text-gray-600 mb-4" data-id="element-1420">
              Probeer andere zoektermen of filters
            </p>
            <Button variant="secondary" onClick={() => {
          setSearchQuery('');
          setActiveFilter('all');
        }} data-id="element-1421">
              Alle thema's tonen
            </Button>
          </div>}
      </div>
      <div className="mt-8 flex justify-center" data-id="element-1422">
        <Button leftIcon={<PlusIcon className="w-4 h-4" data-id="element-1424" />} onClick={() => alert("Nieuwe thema's komen binnenkort beschikbaar")} data-id="element-1423">
          Suggereer een thema
        </Button>
      </div>
    </div>;
};
export default ThemeOverview;